import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Search, Check, Loader2, Sparkles, Car, Tag } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import { formatBusinessDate, formatBusinessTime } from '@/lib/format';

interface Transaction {
    id: number;
    amount: number;
    /** GHS or USD; the amount is printed in its own currency, and bare where this is not given. */
    currency?: string | null;
    transaction_at: string;
    description: string | null;
}

interface AssignTransactionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Single transaction for individual assignment */
    transaction?: Transaction | null;
    /** Multiple transactions for bulk assignment */
    transactions?: Transaction[];
    /** Optional: Contract ID to pre-select */
    preSelectedContractId?: number | null;
}

interface ContractOption {
    contract_id: number;
    driver_id: number;
    driver_name: string;
    driver_photo: string | null;
    vehicle_registration: string | null;
    repayment_tag: string | null;
    is_suggested: boolean;
    suggested_tag: string | null;
}

export function AssignTransactionSheet({
    open,
    onOpenChange,
    transaction,
    transactions = [],
    preSelectedContractId,
}: AssignTransactionSheetProps) {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [updateRepaymentTag, setUpdateRepaymentTag] = useState(true);
    const debouncedSearch = useDebounce(searchTerm, 300);

    // Initial selection
    useEffect(() => {
        if (open && preSelectedContractId) {
            setSelectedContractId(preSelectedContractId);
        } else if (open) {
            setSelectedContractId(null);
        }
        if (open) {
            setUpdateRepaymentTag(true);
        }
    }, [open, preSelectedContractId]);

    // Determine which transactions we're working with
    const allTransactions = transaction ? [transaction] : transactions;
    const isBulk = allTransactions.length > 1;

    // Get the description from first transaction for smart matching
    const descriptionForMatching = allTransactions[0]?.description || null;

    // Fetch active contracts with smart matching
    const { data: contracts, isLoading: isLoadingContracts } = useQuery({
        queryKey: ['active-contracts-for-assignment', descriptionForMatching, debouncedSearch],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_active_contracts_for_assignment', {
                p_description: descriptionForMatching,
                p_search: debouncedSearch || null,
            });
            if (error) throw error;
            return (data || []) as ContractOption[];
        },
        enabled: open,
    });

    // Reset search when sheet opens/closes
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            // selection reset is handled by the other effect or ignored to persist until close
        }
    }, [open]);

    // Assignment mutation
    const assignMutation = useMutation({
        mutationFn: async ({ transactionIds, contractId, driverTagUpdate }: {
            transactionIds: number[];
            contractId: number;
            driverTagUpdate?: { driverId: number; tag: string };
        }) => {
            // Upsert repayment records to handle both new assignments and reassignments
            const { error } = await supabase.from('repayments').upsert(
                transactionIds.map(txId => ({
                    transaction_id: txId,
                    contract_id: contractId,
                })),
                { onConflict: 'transaction_id' }
            );
            if (error) throw error;

            // Update driver's repayment tag if checkbox was checked
            if (driverTagUpdate) {
                const { error: tagError } = await supabase.from('drivers')
                    .update({ repayment_tag: driverTagUpdate.tag })
                    .eq('id', driverTagUpdate.driverId);
                if (tagError) throw tagError;
            }
        },
        onSuccess: () => {
            toast.success(
                isBulk
                    ? `${allTransactions.length} transactions assigned successfully`
                    : 'Transaction assigned successfully'
            );
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['contract-repayments'] });
            queryClient.invalidateQueries({ queryKey: ['active-contracts-for-assignment'] });
            onOpenChange(false);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleAssign = () => {
        if (!selectedContractId || allTransactions.length === 0) return;

        // Build tag update payload if checkbox is checked and suggested driver is selected
        const suggestedContract = suggestedContracts[0];
        const shouldUpdateTag = updateRepaymentTag
            && suggestedContract
            && selectedContractId === suggestedContract.contract_id
            && suggestedContract.suggested_tag;

        assignMutation.mutate({
            transactionIds: allTransactions.map(t => t.id),
            contractId: selectedContractId,
            ...(shouldUpdateTag ? {
                driverTagUpdate: {
                    driverId: suggestedContract.driver_id,
                    tag: suggestedContract.suggested_tag!,
                },
            } : {}),
        });
    };

    // Bare (a number, not cedis) where the caller could not read the currency.
    const formatCurrency = (amount: number, currency: string) => currency
        ? new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount)
        : new Intl.NumberFormat('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

    // One sum per currency: a USD credit added to cedi credits is an amount in neither.
    const totalByCurrency = new Map<string, number>();
    allTransactions.forEach(t => {
        const currency = t.currency || '';
        totalByCurrency.set(currency, (totalByCurrency.get(currency) || 0) + t.amount);
    });
    const totalAmountLabel = Array.from(totalByCurrency.entries())
        .map(([currency, amount]) => formatCurrency(amount, currency))
        .join(' · ') || formatCurrency(0, 'GHS');

    // Separate suggested and other contracts (show only top 1 suggested)
    const allSuggested = contracts?.filter(c => c.is_suggested) || [];
    const suggestedContracts = allSuggested.slice(0, 1);
    const otherContracts = [
        ...allSuggested.slice(1),
        ...(contracts?.filter(c => !c.is_suggested) || [])
    ];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                hideOverlay
                className="w-[500px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
            >
                <SheetHeader>
                    <SheetTitle>
                        {isBulk ? `Assign ${allTransactions.length} Transactions` : 'Assign Transaction'}
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-4 flex flex-col flex-1 min-h-0">
                    {/* Transaction Summary */}
                    <div className="p-4 bg-muted/50 rounded-lg mb-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                                {isBulk ? 'Total Amount' : 'Amount'}
                            </span>
                            <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                {totalAmountLabel}
                            </span>
                        </div>
                        {!isBulk && transaction && (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Date</span>
                                    <span className="text-sm">
                                        {formatBusinessDate(transaction.transaction_at)} · {formatBusinessTime(transaction.transaction_at)}
                                    </span>
                                </div>
                                {transaction.description && (
                                    <div className="pt-2 border-t">
                                        <span className="text-xs text-muted-foreground block mb-1">Description</span>
                                        <p className="text-sm">{transaction.description}</p>
                                    </div>
                                )}
                            </>
                        )}
                        {isBulk && (
                            <p className="text-xs text-muted-foreground">
                                {allTransactions.length} transactions selected
                            </p>
                        )}
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by driver name, contract ID, or plate..."
                            className="pl-9 focus-visible:ring-ring"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Contract List */}
                    <ScrollArea className="flex-1">
                        <div className="space-y-4 pr-4">
                            {/* Suggested Matches */}
                            {suggestedContracts.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                                        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                                        Suggested Match
                                    </div>
                                    {suggestedContracts.map((contract) => (
                                        <ContractCard
                                            key={contract.contract_id}
                                            contract={contract}
                                            isSelected={selectedContractId === contract.contract_id}
                                            onSelect={() => setSelectedContractId(contract.contract_id)}
                                            isSuggested
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Other Contracts */}
                            {otherContracts.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                                        {suggestedContracts.length > 0 ? 'Other Contracts' : 'Active Contracts'}
                                    </div>
                                    {otherContracts.map((contract) => (
                                        <ContractCard
                                            key={contract.contract_id}
                                            contract={contract}
                                            isSelected={selectedContractId === contract.contract_id}
                                            onSelect={() => setSelectedContractId(contract.contract_id)}
                                        />
                                    ))}
                                </div>
                            )}

                            {isLoadingContracts && (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            )}

                            {!isLoadingContracts && contracts?.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    <p className="text-sm">No active contracts found</p>
                                    {searchTerm && (
                                        <p className="text-xs mt-1">Try a different search term</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    {/* Repayment Tag Checkbox + Actions */}
                    <div className="pt-4 border-t mt-4 space-y-3">
                        {/* Show checkbox when suggested driver is selected and has a suggested tag */}
                        {suggestedContracts[0]?.suggested_tag
                            && selectedContractId === suggestedContracts[0]?.contract_id && (
                            <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30">
                                <Checkbox
                                    checked={updateRepaymentTag}
                                    onCheckedChange={(checked) => setUpdateRepaymentTag(checked === true)}
                                    className="mt-0.5"
                                />
                                <span className="text-sm text-muted-foreground">
                                    <Tag className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                                    Update repayment tag to{' '}
                                    <span className="font-medium text-foreground">
                                        &ldquo;{suggestedContracts[0].suggested_tag}&rdquo;
                                    </span>
                                </span>
                            </label>
                        )}
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={handleAssign}
                                disabled={!selectedContractId || assignMutation.isPending}
                            >
                                {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Assign
                            </Button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

// Contract Card Component
function ContractCard({
    contract,
    isSelected,
    onSelect,
    isSuggested = false,
}: {
    contract: ContractOption;
    isSelected: boolean;
    onSelect: () => void;
    isSuggested?: boolean;
}) {
    return (
        <button
            onClick={onSelect}
            className={`w-full text-left p-3 rounded-lg border transition-all hover:bg-muted/50 ${isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : isSuggested
                    ? 'border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/20'
                    : 'border-border'
                }`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{contract.driver_name}</span>
                        {isSuggested && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                <Sparkles className="h-3 w-3 mr-1" />
                                Match
                            </Badge>
                        )}
                        {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span>Contract #{contract.contract_id}</span>
                        {contract.vehicle_registration && (
                            <span className="flex items-center gap-1">
                                <Car className="h-3 w-3" />
                                {contract.vehicle_registration}
                            </span>
                        )}
                    </div>
                    {contract.repayment_tag && (
                        <code className="text-xs text-muted-foreground/70 mt-1 block">
                            Tag: {contract.repayment_tag}
                        </code>
                    )}
                </div>
                <Avatar className="h-12 w-12 shrink-0">
                    <SignedAvatarImage src={contract.driver_photo || undefined} alt={contract.driver_name} />
                    <AvatarFallback className="text-sm">
                        {contract.driver_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                </Avatar>
            </div>
        </button>
    );
}
